import React from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

function isHangulOrEmoji(ch) {
    if (!ch) return false;
    if (/\p{Script=Hangul}/u.test(ch) || /\p{Emoji}/u.test(ch)) return true;
    return false;
}

function preprocessMarkdown(text) {
    if (typeof text !== 'string') return text;

    // Normalize newlines for consistent regex behavior
    let result = text.replace(/\r\n/g, '\n');

    // 1) --- 뒤에 줄바꿈 보정 (기존 로직 유지)
    result = result.replace(/---(\S)/g, '---\n\n$1');

    // 1-1) 밑줄 헤더(Setext heading) 방지:
    result = result.replace(/([^\n])\n([ \t]*-{3,}[ \t]*)\n/g, '$1\n\n$2\n');
    result = result.replace(/\n([ \t]*-{3,}[ \t]*)\n(?!\n)/g, '\n$1\n\n');

    // 2) bold 뒤에 붙은 한글/이모지에만 공백 추가
    let out = '';
    let inBold = false; // 현재 ** 안에 있는지
    for (let i = 0; i < result.length; ) {
        // "**" 발견
        if (result[i] === '*' && result[i + 1] === '*') {
            // bold 토글
            inBold = !inBold;
            out += '**';
            i += 2;

            // 방금 닫는 쪽을 통과했으면 (inBold가 false가 됐으면)
            if (!inBold) {
                const    cp = result.codePointAt(i);
                const nextChar = cp != null ? String.fromCodePoint(cp) : '';
                // 다음 문자가 한글/이모지고, 이미 공백이 하나 붙어 있지 않다면
                if (nextChar && isHangulOrEmoji(nextChar)) {
                    const lastOutChar = out[out.length - 1];
                    if (lastOutChar !== ' ' && lastOutChar !== '\n') {
                        out += ' ';
                    }
                }
            }
            continue;
        }

        // 일반 문자
        out += result[i];
        i += 1;
    }

    return out;
}

function normalizeHref(href) {
    if (typeof href !== 'string') return href;
    const trimmed = href.trim();
    if (!trimmed) return trimmed;

    // If it already has a URI scheme (http:, https:, mailto:, tel:, etc.), keep as-is
    if (/^[a-zA-Z][a-zA-Z\d+\-.]*:/.test(trimmed)) return trimmed;

    // Protocol-relative URL
    if (trimmed.startsWith('//')) return `https:${trimmed}`;

    // Domain-like (e.g. www.example.com/path or example.com)
    if (/^[^\s]+\.[^\s]+/.test(trimmed)) return `https://${trimmed}`;

    return trimmed;
}


/**
 * children 전체를 문자열로 가정하지 않고,
 * ReactNode 트리를 순회해 문자열을 안전하게 추출
 */
function extractText(node) {
    if (typeof node === 'string') return node;
    if (Array.isArray(node)) return node.map(extractText).join('');

    if (React.isValidElement(node)) {
        return extractText(node.props.children);
    }
    return '';
}

/**
 * <ul> 커스텀: children 구조 수정 x
 */
const CustomUl = ({ children, ...props }) => {
    return (
        <ul
            className="h3 fc-gray-800"
            style={{
                listStyleType: 'disc',
                paddingLeft: '0',
                whiteSpace: 'normal'
            }}
            {...props}
        >
            {children}
        </ul>
    );
};

/**
 * <li> 커스텀: children 구조 수정 x
 */
const CustomLi = ({ children, ...props }) => {
    return (
        <li
            className="h3 fc-gray-800"
            style={{ marginLeft: '20px', marginBottom: '0' }}
            {...props}
        >
            {children}
        </li>
    );
};

/**
 * <p> 커스텀: children 구조 수정 x
 */
const CustomP = ({ children, ...props }) => {
    return (
        <p
            className="h3 fc-gray-800"
            style={{ whiteSpace: 'normal' }}
            {...props}
        >
            {children}
        </p>
    );
};

/**
 * <strong> 커스텀(원한다면 스타일 추가 가능)
 * 매뉴얼 split 불안정해서 일단 react-markdown 로직만 사용
 */
const CustomStrong = ({ children }) => {
    return (
        <strong style={{ fontWeight: 700 }}>
            {children}
        </strong>
    );
};

/**
 * 링크 special 처리:
 * 문자열을 split하지 않고 ReactNode 트리에서 텍스트만 추출해서 체크
 */
const CustomA = ({ children, ...props }) => {
    const textContent = extractText(children);
    const isSpecial = textContent.includes(':link:') || textContent.includes('🔗');
    const normalizedHref = normalizeHref(props.href);

    if (isSpecial) {
        return (
            <a
                {...props}
                href={normalizedHref}
                target="_blank"
                rel="noopener noreferrer"
                className="special-link h3 fc-gray-800"
            >
                {children}
            </a>
        );
    }

    return (
        <a
            {...props}
            href={undefined}
            onClick={(e) => e.preventDefault()}
            style={{ cursor: 'default' }}
            className="h3 fc-gray-800"
        >
            {children}
        </a>
    );
};

/**
 * <del> 커스텀
 */
const CustomDel = ({ children }) => {
    const textContent = extractText(children);
    return <>~{textContent}~</>;
};

/**
 * 최종 Markdown Converter
 */
const MarkdownConverter = ({ text }) => {
    const processedText = preprocessMarkdown(text);

    return (
        <Markdown
            remarkPlugins={[remarkGfm]}
            components={{
                ul: CustomUl,
                li: CustomLi,
                p: CustomP,
                a: CustomA,
                strong: CustomStrong,
                del: CustomDel,
            }}
        >
            {processedText}
        </Markdown>
    );
};

export default MarkdownConverter;